import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useLocale } from "../i18n/LocaleContext";

const PRIVACY_EMAIL = "privacy@garagedrops.com";
const COOKIEBOT_CBID = "facaa78c-5571-4857-a8e0-c8d2219f2c40";

/**
 * Injects the Cookiebot Cookie Declaration script into the given container.
 * The script renders an auto-updated table of every cookie used on the site,
 * grouped by category (necessary, statistics, marketing…). Required by GDPR
 * for full transparency alongside the consent banner.
 */
function CookieDeclaration() {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    // Avoid duplicate injection on re-render / dev hot-reload.
    if (el.querySelector("script#CookieDeclaration")) return;
    const script = document.createElement("script");
    script.id = "CookieDeclaration";
    script.src = `https://consent.cookiebot.com/${COOKIEBOT_CBID}/cd.js`;
    script.type = "text/javascript";
    script.async = true;
    el.appendChild(script);
  }, []);

  return (
    <div
      ref={containerRef}
      className="mt-6"
      data-testid="cookie-declaration"
      style={{ minHeight: 20 }}
    />
  );
}

function Section({ title, children, id }) {
  return (
    <section id={id}>
      <h2 className="text-2xl font-light mb-4" style={{ color: "var(--text-primary)" }}>
        {title}
      </h2>
      {children}
    </section>
  );
}

export default function Privacy() {
  const { t } = useLocale();
  const list2 = t("privacy.s2List");
  const list7 = t("privacy.s7List");

  return (
    <div className="min-h-screen pt-32 pb-24 px-6 sm:px-12" data-testid="privacy-page">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="max-w-3xl mx-auto"
      >
        <span className="overline mb-6 block">{t("privacy.overline")}</span>
        <h1 className="text-4xl sm:text-6xl font-light mb-4" style={{ letterSpacing: "-0.01em" }}>
          {t("privacy.title")}
        </h1>
        <p className="text-sm mb-12" style={{ color: "var(--text-secondary)" }}>
          {t("privacy.updated")}
        </p>

        <div
          className="space-y-10 text-sm sm:text-base leading-relaxed"
          style={{ color: "var(--text-secondary)" }}
        >
          <Section title={t("privacy.s1Title")}>
            <p>
              {t("privacy.s1Body")}{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="gd-link">
                {PRIVACY_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section title={t("privacy.s2Title")}>
            <p>{t("privacy.s2Intro")}</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              {(Array.isArray(list2) ? list2 : []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section title={t("privacy.s3Title")}>
            <p>{t("privacy.s3Body")}</p>
          </Section>

          <Section title={t("privacy.s4Title")} id="cookies">
            <p>{t("privacy.s4Body")}</p>
            <p className="mt-4">
              Il consenso ai cookie è gestito tramite{" "}
              <a
                href="https://www.cookiebot.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="gd-link"
              >
                Cookiebot
              </a>{" "}
              (certificato ISO 27001), con blocco preventivo automatico e{" "}
              <strong>Google Consent Mode v2</strong> attivo. Nessun cookie
              non essenziale viene installato prima del tuo consenso.
              Puoi modificare o revocare le tue preferenze in qualsiasi
              momento tramite il pulsante &quot;Preferenze cookie&quot; presente
              nel banner.
            </p>
            <p className="mt-4">
              L&apos;elenco completo dei cookie utilizzati (aggiornato
              automaticamente ad ogni scansione mensile) è consultabile
              qui sotto:
            </p>
            <CookieDeclaration />
          </Section>

          <Section title="Google Ads e Conversioni Avanzate" id="google-ads">
            <p>
              Quando dai il consenso ai cookie di <strong>marketing</strong>,
              utilizziamo <strong>Google Ads</strong> (Google Ireland Limited,
              Gordon House, Barrow Street, Dublino 4, Irlanda) per misurare
              l&apos;efficacia delle nostre campagne pubblicitarie e per
              rilevare le conversioni (ad esempio la registrazione di un
              nuovo membro o l&apos;invio del modulo contatti).
            </p>
            <p className="mt-4">
              È attiva anche la funzionalità <strong>Conversioni Avanzate
              (Enhanced Conversions)</strong>: quando completi
              un&apos;azione considerata conversione, il tuo indirizzo email
              viene sottoposto a <em>hashing SHA-256</em> nel tuo browser
              (l&apos;email in chiaro non lascia mai il tuo dispositivo) e
              il valore hashato viene trasmesso a Google Ads per abbinare
              la conversione a un click su un annuncio. Questo permette a
              Google di attribuire correttamente i risultati anche quando i
              cookie di tracciamento tradizionali non sono disponibili
              (browser privati, dispositivi diversi, ecc.).
            </p>
            <p className="mt-4">
              La base giuridica è il tuo <strong>consenso esplicito</strong>{" "}
              (art. 6 par. 1 lett. a GDPR). Puoi revocarlo in qualsiasi
              momento dal pulsante &quot;Preferenze cookie&quot; nel footer:
              la revoca disattiva sia il tracciamento standard sia le
              Conversioni Avanzate per le sessioni future.
            </p>
            <p className="mt-4">
              Google agisce come <strong>Responsabile del trattamento</strong>{" "}
              ai sensi dell&apos;art. 28 GDPR, secondo i{" "}
              <a
                href="https://business.safety.google/adsprocessorterms/"
                target="_blank"
                rel="noopener noreferrer"
                className="gd-link"
              >
                Google Ads Data Processing Terms
              </a>
              . Per informazioni sui trattamenti effettuati da Google
              consulta la{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="gd-link"
              >
                Privacy Policy di Google
              </a>
              .
            </p>
          </Section>

          <Section title={t("privacy.s5Title")}>
            <p>{t("privacy.s5Body")}</p>
          </Section>

          <Section title={t("privacy.s6Title")}>
            <p>{t("privacy.s6Body")}</p>
          </Section>

          <Section title={t("privacy.s7Title")}>
            <p>{t("privacy.s7Intro")}</p>
            <ul className="list-disc pl-6 mt-3 space-y-2">
              {(Array.isArray(list7) ? list7 : []).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p className="mt-3">
              {t("privacy.s7Outro")}{" "}
              <a href={`mailto:${PRIVACY_EMAIL}`} className="gd-link">
                {PRIVACY_EMAIL}
              </a>
              .
            </p>
          </Section>

          <Section title={t("privacy.s8Title")}>
            <p>{t("privacy.s8Body")}</p>
          </Section>

          <Section title={t("privacy.s9Title")} id="terms">
            <p>{t("privacy.s9Body")}</p>
          </Section>

          <Section title={t("privacy.s10Title")}>
            <p>{t("privacy.s10Body")}</p>
          </Section>
        </div>
      </motion.div>
    </div>
  );
}
